import React from 'react';

/**
 * lazyPage — React.lazy with one safety net for deployments.
 *
 * Chunk filenames carry a content hash. When a new version is deployed, a tab
 * that loaded the OLD main bundle will ask for OLD chunk names that no longer
 * exist, and the import fails ("Loading chunk N failed" / a fetch error).
 * Without handling, that tab shows a broken page until the person refreshes.
 *
 * Here a failed chunk import is retried once after a short pause (a transient
 * network blip), and if it still fails the boundary below reloads the page
 * once — which fetches the new main bundle and the right chunk names. A flag
 * in sessionStorage prevents a reload loop.
 */
const registeredPages = [];   // every page importer, in route order

export function lazyPage(importer) {
  registeredPages.push(importer);
  return React.lazy(() =>
    importer().catch((err) =>
      new Promise((resolve) => setTimeout(resolve, 800)).then(() => importer()).catch(() => {
        const e = new Error(err && err.message ? err.message : 'chunk load failed');
        e.name = 'ChunkLoadError';
        throw e;
      })
    )
  );
}

/**
 * warmPages — download every page chunk in the background, soon after the
 * app has drawn, so that the first click on a page finds its code already
 * in the browser.
 *
 * Why (24 Sep 2026, the day the split shipped): before the split the whole
 * app arrived in one file, so moving between pages never touched the
 * network. With one chunk per page, the first visit to each page had to
 * fetch it — and through Cloudflare a chunk not yet at the edge costs
 * 0.5-1.5 s (occasionally several seconds), so every page felt slower on
 * first use, and again after every deploy when all chunk names change.
 *
 * Pages are fetched in route order (dashboard first), a few at a time, and
 * only after a short pause so the page the person is actually looking at
 * gets the network first. A chunk that fails here is simply skipped — the
 * click-time path (retry, then one reload) still handles a stale tab.
 * Honours the browser's data-saver setting.
 */
let warmed = false;
export function warmPages({ delayMs = 2500, concurrency = 3 } = {}) {
  if (warmed) return;
  warmed = true;
  try {
    const c = typeof navigator !== 'undefined' && navigator.connection;
    if (c && c.saveData) return;
  } catch (_) { /* ignore */ }
  const run = () => {
    const queue = registeredPages.slice();
    const next = () => {
      const importer = queue.shift();
      if (!importer) return;
      Promise.resolve().then(importer).catch(() => {}).then(next);
    };
    for (let i = 0; i < concurrency; i += 1) next();
  };
  const start = () => {
    if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: 4000 });
    } else {
      run();
    }
  };
  setTimeout(start, delayMs);
}

/** Centred spinner shown while a page's chunk downloads. */
export const PageFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <div style={{ width: 28, height: 28, border: '3px solid rgba(0,200,155,0.25)', borderTopColor: 'var(--teal, #00c89b)', borderRadius: '50%', animation: 'kodahfy-spin 0.9s linear infinite' }} />
    <style>{'@keyframes kodahfy-spin { to { transform: rotate(360deg); } }'}</style>
  </div>
);

const RELOAD_FLAG = 'kodahfy_chunk_reload';

export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    const isChunk = error && (error.name === 'ChunkLoadError' || /Loading chunk|Loading CSS chunk|Failed to fetch dynamically imported module|import\(\)/i.test(String(error.message)));
    if (!isChunk) return;
    let alreadyReloaded = false;
    try { alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch (_) { /* ignore */ }
    if (!alreadyReloaded) {
      try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch (_) { /* ignore */ }
      window.location.reload();
    }
  }

  componentDidUpdate() {
    // A page rendered fine after a reload: clear the flag so a future deploy
    // can reload once again.
    if (!this.state.error) {
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) { /* ignore */ }
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 32, textAlign: 'center', fontFamily: 'inherit' }}>
          <p style={{ fontSize: 16, marginBottom: 12 }}>This page could not be loaded.</p>
          <button
            type="button"
            onClick={() => { try { sessionStorage.removeItem(RELOAD_FLAG); } catch (_) { /* ignore */ } window.location.reload(); }}
            style={{ padding: '8px 16px', cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
